// Admin-only Users management page.
//
// Lets an admin (david@arkade.ai) see every pool user and manage their access:
//   - grant/revoke the six dashboard sections (or full Admin) inline per row,
//   - enable/disable an account (disable also global-signs-them-out),
//   - reset a password (new password shown ONCE — never persisted/refetchable),
//   - create a new user (email + grants → one-time password).
//
// The SERVER is the real guard (admin-only routes + bootstrap/self protection);
// the disabled controls + tooltips here are UI convenience. Any 403 the server
// returns (forbidden_bootstrap_admin / cannot_disable_self) is surfaced near the
// offending row rather than crashing.
//
// One-time-secret UX (the password reveal) is copied in structure from
// MirrorClientNew.tsx: a highlighted card, monospace value, copy button, and a
// "you will not see this again" line.

import { useEffect, useState } from "react";

import { api, ApiError, type AdminUserRow, type Section } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  type PillTone,
  Skeleton,
  tokens,
} from "../lib/theme";

// ---------------------------------------------------------------------------
// Section metadata — ordered ids + human labels matching the nav headings.
// ---------------------------------------------------------------------------

const SECTION_ORDER: Section[] = [
  "debt-lookup",
  "inbound-rtb",
  "legal-widget",
  "sms",
  "logs",
  "reporting",
  "dialer",
  "lead-sales",
  "yes",
];

const SECTION_LABELS: Record<Section, string> = {
  "debt-lookup": "Debt Lookup",
  "inbound-rtb": "Inbound RTB",
  "legal-widget": "Legal Widget",
  sms: "SMS",
  logs: "Logs",
  reporting: "Reporting",
  dialer: "Dialer",
  "lead-sales": "Lead Sales",
  yes: "Yes",
};

const SELF_LOCK_TITLE = "You can't disable or de-admin your own account";

// ---------------------------------------------------------------------------
// Error mapping — prefer a friendly message for a known server {error} code,
// else fall back to ApiError.message (which already includes the code/detail).
// ---------------------------------------------------------------------------

const ERROR_MESSAGES: Record<string, string> = {
  user_exists: "A user with that email already exists.",
  forbidden_bootstrap_admin:
    "This is the protected bootstrap admin — it can't be disabled or de-admined.",
  cannot_disable_self: "You can't disable or de-admin your own account.",
  forbidden_section: "You don't have permission to do that.",
  invalid_body: "Some fields are invalid — check the email and try again.",
  user_not_found: "That user no longer exists — reload the list.",
  authz_unavailable:
    "The authorization service is temporarily unavailable. Try again shortly.",
};

function serverErrorCode(err: unknown): string {
  if (
    err instanceof ApiError &&
    err.body &&
    typeof err.body === "object" &&
    "error" in err.body
  ) {
    const code = (err.body as { error?: unknown }).error;
    return typeof code === "string" ? code : "";
  }
  return "";
}

function friendlyError(err: unknown): string {
  const code = serverErrorCode(err);
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "Something went wrong.";
}

function statusTone(status: string): PillTone {
  if (status === "CONFIRMED") return "success";
  if (status === "FORCE_CHANGE_PASSWORD" || status === "RESET_REQUIRED") {
    return "warning";
  }
  return "neutral";
}

function sameSectionSet(a: readonly Section[], b: readonly Section[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((s) => setB.has(s));
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

// Draft grant state edited by the GrantEditor (shared by create + edit).
interface GrantDraft {
  sections: Section[];
  isAdmin: boolean;
}

// A pending one-time password reveal (create or reset). Never persisted.
interface RevealState {
  password: string;
  email: string;
  kind: "created" | "reset";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Users() {
  const { user, refreshGrants } = useAuth();
  const currentSub = user?.sub ?? null;

  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState<RevealState | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listUsers()
      .then((d) => {
        if (!cancelled) {
          setRows(d);
          setLoadError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(friendlyError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRowChange = (updated: AdminUserRow): void => {
    setRows((rs) =>
      rs ? rs.map((r) => (r.sub === updated.sub ? updated : r)) : rs,
    );
  };

  const handleCreated = (newRow: AdminUserRow, password: string): void => {
    setRows((rs) => (rs ? [newRow, ...rs] : [newRow]));
    setReveal({ password, email: newRow.email, kind: "created" });
    setCreating(false);
  };

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage who can access each dashboard section"
        actions={
          <button
            type="button"
            onClick={() => setCreating((c) => !c)}
            style={buttonStyle(creating ? "ghost" : "primary")}
          >
            {creating ? "Close" : "+ New user"}
          </button>
        }
      />

      {reveal && (
        <PasswordReveal reveal={reveal} onDismiss={() => setReveal(null)} />
      )}

      {creating && (
        <CreateUserForm
          onCreated={handleCreated}
          onCancel={() => setCreating(false)}
        />
      )}

      {loading && !rows && (
        <Card>
          <Skeleton height={64} />
          <div style={{ height: 12 }} />
          <Skeleton height={64} />
          <div style={{ height: 12 }} />
          <Skeleton height={64} />
        </Card>
      )}

      {loadError && !rows && (
        <Card>
          <EmptyState title="Failed to load users" hint={loadError} />
        </Card>
      )}

      {rows && rows.length === 0 && (
        <Card>
          <EmptyState
            title="No users yet"
            hint="Create the first user to grant dashboard access."
          />
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div style={{ display: "grid", gap: tokens.space.lg }}>
          {rows.map((row) => (
            <UserCard
              key={row.sub}
              row={row}
              isSelf={currentSub !== null && currentSub === row.sub}
              refreshGrants={refreshGrants}
              onRowChange={handleRowChange}
              onReveal={setReveal}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PasswordReveal — one-time generated-password card (create + reset).
// ---------------------------------------------------------------------------

function PasswordReveal({
  reveal,
  onDismiss,
}: {
  reveal: RevealState;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(reveal.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked */
    }
  };

  const heading =
    reveal.kind === "reset"
      ? `New password for ${reveal.email}`
      : `User created — ${reveal.email}`;

  const note =
    reveal.kind === "reset"
      ? "Share this out-of-band. The user re-logs in with it and — if their TOTP device was reset — re-enrolls their authenticator at first login."
      : "Share this out-of-band. The user signs in with it and enrolls their TOTP authenticator at first login.";

  return (
    <Card style={{ marginBottom: tokens.space.lg }}>
      <div
        style={{
          fontSize: tokens.font.size.lg,
          fontWeight: 600,
          color: tokens.color.fg0,
          marginBottom: tokens.space.md,
        }}
      >
        {heading}
      </div>
      <div
        style={{
          padding: tokens.space.lg,
          background: tokens.color.bg1,
          border: "1px solid rgba(0,212,170,0.30)",
          borderRadius: tokens.radius.md,
        }}
      >
        <div
          style={{
            fontSize: tokens.font.size.xs,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tokens.color.tealLight,
            fontWeight: 600,
            marginBottom: tokens.space.sm,
          }}
        >
          One-time password
        </div>
        <pre
          style={{
            margin: 0,
            padding: "10px 12px",
            background: tokens.color.bg0,
            color: tokens.color.tealLight,
            borderRadius: tokens.radius.sm,
            fontFamily: tokens.font.mono,
            fontSize: tokens.font.size.sm,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            userSelect: "all",
          }}
        >
          <code>{reveal.password}</code>
        </pre>
        <div
          style={{
            marginTop: tokens.space.sm,
            display: "flex",
            gap: tokens.space.sm,
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={() => void onCopy()}
            style={buttonStyle("subtle")}
          >
            {copied ? "Copied ✓" : "Copy"}
          </button>
          <span
            style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}
          >
            You won't be able to see this again.
          </span>
        </div>
      </div>
      <div
        style={{
          marginTop: tokens.space.md,
          color: tokens.color.fg1,
          fontSize: tokens.font.size.sm,
        }}
      >
        {note}
      </div>
      <div style={{ marginTop: tokens.space.lg }}>
        <button type="button" onClick={onDismiss} style={buttonStyle("ghost")}>
          Dismiss
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// GrantEditor — six section checkboxes + an Admin toggle. Admin implies full
// access, so it greys/disables the section boxes while on. Controlled.
// ---------------------------------------------------------------------------

function GrantEditor({
  idPrefix,
  value,
  onChange,
  disabled = false,
  adminLocked = false,
  adminLockTitle,
}: {
  idPrefix: string;
  value: GrantDraft;
  onChange: (next: GrantDraft) => void;
  disabled?: boolean;
  adminLocked?: boolean;
  adminLockTitle?: string;
}) {
  const sectionsDisabled = disabled || value.isAdmin;

  const toggleSection = (section: Section, checked: boolean): void => {
    const next = checked
      ? [...value.sections, section]
      : value.sections.filter((s) => s !== section);
    onChange({ ...value, sections: next });
  };

  return (
    <div>
      <label
        title={adminLocked ? adminLockTitle : undefined}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: tokens.space.sm,
          cursor: disabled || adminLocked ? "not-allowed" : "pointer",
          marginBottom: tokens.space.md,
        }}
      >
        <input
          type="checkbox"
          checked={value.isAdmin}
          disabled={disabled || adminLocked}
          onChange={(e) => onChange({ ...value, isAdmin: e.target.checked })}
          style={{ marginTop: 3 }}
        />
        <span>
          <span
            style={{
              color: tokens.color.fg0,
              fontSize: tokens.font.size.base,
              fontWeight: 600,
            }}
          >
            Admin
          </span>
          <span
            style={{
              display: "block",
              color: tokens.color.fg1,
              fontSize: tokens.font.size.sm,
              marginTop: 2,
            }}
          >
            Full access to every section plus user management.
          </span>
        </span>
      </label>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: tokens.space.sm,
          opacity: sectionsDisabled ? 0.5 : 1,
        }}
      >
        {SECTION_ORDER.map((section) => {
          const id = `${idPrefix}-${section}`;
          const checked = value.isAdmin || value.sections.includes(section);
          return (
            <label
              key={section}
              htmlFor={id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.space.sm,
                cursor: sectionsDisabled ? "not-allowed" : "pointer",
                color: tokens.color.fg0,
                fontSize: tokens.font.size.base,
              }}
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                disabled={sectionsDisabled}
                onChange={(e) => toggleSection(section, e.target.checked)}
              />
              {SECTION_LABELS[section]}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreateUserForm — email + grants → one-time password.
// ---------------------------------------------------------------------------

function CreateUserForm({
  onCreated,
  onCancel,
}: {
  onCreated: (row: AdminUserRow, password: string) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const [draft, setDraft] = useState<GrantDraft>({
    sections: [],
    isAdmin: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.createUser({
        email: email.trim(),
        sections: draft.sections,
        is_admin: draft.isAdmin,
      });
      onCreated(res.user, res.password);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="New user" style={{ marginBottom: tokens.space.lg }}>
      <form onSubmit={onSubmit}>
        <div style={{ maxWidth: 360, marginBottom: tokens.space.lg }}>
          <FieldLabel htmlFor="new-user-email">Email</FieldLabel>
          <input
            id="new-user-email"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </div>

        <FieldLabel>Access</FieldLabel>
        <GrantEditor
          idPrefix="new-user"
          value={draft}
          onChange={setDraft}
          disabled={submitting}
        />

        {error && (
          <div
            role="alert"
            style={{
              marginTop: tokens.space.lg,
              padding: tokens.space.md,
              background: tokens.color.dangerBg,
              border: `1px solid ${tokens.color.dangerBorder}`,
              color: tokens.color.danger,
              borderRadius: tokens.radius.sm,
              fontSize: tokens.font.size.sm,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: tokens.space.lg,
            display: "flex",
            gap: tokens.space.sm,
          }}
        >
          <button
            type="submit"
            disabled={submitting || email.trim() === ""}
            style={buttonStyle(
              "primary",
              submitting || email.trim() === "",
            )}
          >
            {submitting ? "Creating…" : "Create user"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={buttonStyle("ghost", submitting)}
          >
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// UserCard — one pool user: identity + badges, inline grant editor, actions.
// ---------------------------------------------------------------------------

function UserCard({
  row,
  isSelf,
  refreshGrants,
  onRowChange,
  onReveal,
}: {
  row: AdminUserRow;
  isSelf: boolean;
  refreshGrants: () => Promise<void>;
  onRowChange: (row: AdminUserRow) => void;
  onReveal: (reveal: RevealState) => void;
}) {
  const [draft, setDraft] = useState<GrantDraft>({
    sections: row.sections,
    isAdmin: row.is_admin,
  });
  const [savingGrants, setSavingGrants] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const dirty =
    draft.isAdmin !== row.is_admin ||
    !sameSectionSet(draft.sections, row.sections);
  const busy = savingGrants || togglingEnabled || resetting;
  // Clicking the enable/disable button while enabled would DISABLE the account;
  // that is the guarded direction for the current user's own row.
  const blockSelfDisable = isSelf && row.enabled;

  const onSaveGrants = async (): Promise<void> => {
    setRowError(null);
    setSavingGrants(true);
    try {
      const updated = await api.putUserSections(row.sub, {
        sections: draft.sections,
        is_admin: draft.isAdmin,
      });
      onRowChange(updated);
      setDraft({ sections: updated.sections, isAdmin: updated.is_admin });
      // If we just edited our own grants, refresh the caller's live nav.
      if (isSelf) await refreshGrants();
    } catch (err) {
      setRowError(friendlyError(err));
    } finally {
      setSavingGrants(false);
    }
  };

  const onToggleEnabled = async (): Promise<void> => {
    setRowError(null);
    setTogglingEnabled(true);
    try {
      const updated = row.enabled
        ? await api.disableUser(row.sub)
        : await api.enableUser(row.sub);
      onRowChange(updated);
    } catch (err) {
      setRowError(friendlyError(err));
    } finally {
      setTogglingEnabled(false);
    }
  };

  const onResetPassword = async (): Promise<void> => {
    setRowError(null);
    setResetting(true);
    try {
      const { password } = await api.resetUserPassword(row.sub);
      onReveal({ password, email: row.email, kind: "reset" });
    } catch (err) {
      setRowError(friendlyError(err));
    } finally {
      setResetting(false);
    }
  };

  const created = fmtDate(row.created_at);

  return (
    <Card>
      {/* Identity + status badges */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: tokens.space.md,
          marginBottom: tokens.space.lg,
        }}
      >
        <div>
          <div
            style={{
              color: tokens.color.fg0,
              fontWeight: 600,
              fontSize: tokens.font.size.lg,
            }}
          >
            {row.email}
            {isSelf && (
              <span
                style={{
                  marginLeft: tokens.space.sm,
                  color: tokens.color.fg2,
                  fontSize: tokens.font.size.xs,
                  fontWeight: 500,
                }}
              >
                (you)
              </span>
            )}
          </div>
          <div
            style={{
              color: tokens.color.fg2,
              fontSize: tokens.font.size.xs,
              fontFamily: tokens.font.mono,
              marginTop: 2,
            }}
          >
            {row.sub}
            {created ? ` · created ${created}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
          <Pill tone={statusTone(row.status)}>{row.status}</Pill>
          <Pill tone={row.enabled ? "neutral" : "danger"}>
            {row.enabled ? "Enabled" : "Disabled"}
          </Pill>
          {row.is_admin && <Pill tone="warning">Admin</Pill>}
        </div>
      </div>

      {/* Current access (saved state) */}
      <div style={{ marginBottom: tokens.space.lg }}>
        <div
          style={{
            fontSize: tokens.font.size.xs,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tokens.color.fg1,
            fontWeight: 600,
            marginBottom: tokens.space.sm,
          }}
        >
          Access
        </div>
        {row.is_admin ? (
          <Pill tone="success">All sections</Pill>
        ) : row.sections.length > 0 ? (
          <div
            style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}
          >
            {SECTION_ORDER.filter((s) => row.sections.includes(s)).map((s) => (
              <Pill key={s}>{SECTION_LABELS[s]}</Pill>
            ))}
          </div>
        ) : (
          <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
            No sections granted yet.
          </span>
        )}
      </div>

      {/* Inline grant editor */}
      <div
        style={{
          borderTop: `1px solid ${tokens.color.border}`,
          paddingTop: tokens.space.lg,
        }}
      >
        <FieldLabel>Edit grants</FieldLabel>
        <GrantEditor
          idPrefix={`grant-${row.sub}`}
          value={draft}
          onChange={setDraft}
          disabled={busy}
          adminLocked={isSelf}
          adminLockTitle={SELF_LOCK_TITLE}
        />
        <div
          style={{
            marginTop: tokens.space.md,
            display: "flex",
            gap: tokens.space.sm,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => void onSaveGrants()}
            disabled={busy || !dirty}
            style={buttonStyle("primary", busy || !dirty)}
          >
            {savingGrants ? "Saving…" : "Save grants"}
          </button>
          {dirty && !savingGrants && (
            <button
              type="button"
              onClick={() =>
                setDraft({ sections: row.sections, isAdmin: row.is_admin })
              }
              disabled={busy}
              style={buttonStyle("ghost", busy)}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Account actions */}
      <div
        style={{
          borderTop: `1px solid ${tokens.color.border}`,
          marginTop: tokens.space.lg,
          paddingTop: tokens.space.lg,
          display: "flex",
          gap: tokens.space.sm,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={() => void onToggleEnabled()}
          disabled={busy || blockSelfDisable}
          title={blockSelfDisable ? SELF_LOCK_TITLE : undefined}
          style={buttonStyle(
            row.enabled ? "danger" : "subtle",
            busy || blockSelfDisable,
          )}
        >
          {togglingEnabled
            ? "Working…"
            : row.enabled
              ? "Disable"
              : "Enable"}
        </button>
        <button
          type="button"
          onClick={() => void onResetPassword()}
          disabled={busy}
          style={buttonStyle("ghost", busy)}
        >
          {resetting ? "Resetting…" : "Reset password"}
        </button>
        {row.enabled && (
          <span
            style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}
          >
            Disabling also signs the user out of all sessions.
          </span>
        )}
      </div>

      {rowError && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.lg,
            padding: tokens.space.md,
            background: tokens.color.dangerBg,
            border: `1px solid ${tokens.color.dangerBorder}`,
            color: tokens.color.danger,
            borderRadius: tokens.radius.sm,
            fontSize: tokens.font.size.sm,
          }}
        >
          {rowError}
        </div>
      )}
    </Card>
  );
}
